/**
 * The one line a location shows about its 60-day bank window, on the red
 * banner and on the location bank card. Pure, so the copy for every state is
 * unit-tested (bankWindowCopy.test.ts); BankWindowText only renders it.
 *
 * Shown only while the location's bank is NOT ready (both callers filter on
 * that), so every state here is "something is still needed".
 *
 * The states, in the order they are decided:
 * 0. Bank status unavailable (Stripe could not be reached): say only that.
 *    Not-ready here means "unknown", not "broken" or "missing".
 * 1. Verified once, not ready now: the account broke after it was connected.
 *    Site Host Terms section 8.7 gives 60 days from then to restore it, with
 *    reimbursement still accruing, so neither window line is true.
 * 2. The server's enrolment state (Site Host Terms pre-flip, 2026-10-02). A
 *    countdown runs ONLY for an enrolled location:
 *    a. No countdown for an existing agreement (launched before 2 Oct 2026, no
 *       Transition Notice yet), a launch the ledger does not track (reimbursed
 *       by EV Initiative directly), or a home (settled with each session).
 *    b. Transition Notice recorded, date still ahead: say when the 60 days start.
 *    c. Not launched: ask for the launch date.
 *    d. Enrolled: the countdown, then Warren's closed-window rule.
 * 3. An older API without the enrolment state keeps the previous rules.
 *
 * The words live in payoutSetupCopy.ts. bankWindowCopy and bankBannerHeadline
 * take an optional locale (payoutLocale() on the screen) and speak English
 * when it is left out; the exported constants are the English lines.
 */
import { SITE_HOST_TERMS_FIRST_LAUNCH_AT } from '@evi/shared';
import { daysRemainingKey, fillPayoutCopy, payoutDateLocale, payoutSetupCopy } from './payoutSetupCopy';

export type ElectricityEnrollment =
  | 'enrolled' | 'transition_scheduled' | 'existing_agreement' | 'manual' | 'not_launched' | 'owner_site';

export interface BankWindowInfo {
  launch_at: string | null;
  inside_window: boolean;
  days_remaining: number | null;
  policy_version: string | null;
  first_verified_at?: string | null;
  /** The live bank status; 'unavailable' when Stripe could not be checked. */
  status?: string;
  /** Absent on an API from before 2 Oct 2026. */
  enrollment?: ElectricityEnrollment;
  start_basis?: 'launch' | 'transition' | null;
  /** Absent on older APIs; null means no site-manager bank-access grant yet. */
  grace_anchor_at?: string | null;
  /** The Transition Date as a calendar date (YYYY-MM-DD). */
  transition_on?: string | null;
}

const EN = payoutSetupCopy('en');
export const BANK_WINDOW_CLOSED_COPY = EN.bankWindowClosed;
export const BANK_EXISTING_AGREEMENT_COPY = EN.bankExistingAgreement;
export const BANK_NEEDS_RESTORE_COPY = EN.bankNeedsRestore;
export const BANK_MANUAL_COPY = EN.bankManual;
export const BANK_OWNER_SITE_COPY = EN.bankOwnerSite;
export const BANK_STATUS_UNKNOWN_COPY = EN.bankStatusUnknown;
export const BANK_LAUNCH_UNKNOWN_COPY = EN.bankLaunchUnknown;
export const BANK_ACCESS_NOT_YET_GRANTED_COPY = EN.bankAccessNotYetGranted;
export const BANK_BANNER_LAUNCH_HEADLINE = EN.bankBannerLaunchHeadline;
export const BANK_BANNER_WINDOW_HEADLINE = EN.bankBannerWindowHeadline;

const FIRST_LAUNCH_MS = Date.parse(SITE_HOST_TERMS_FIRST_LAUNCH_AT);

/** "November 1, 2026" from a calendar date, the same in every browser zone. `locale` defaults to that en-US form. */
export function formatCalendarDay(ymd: string, locale = 'en-US'): string {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(y, m - 1, d)));
}

function countdown(info: BankWindowInfo, locale: unknown): string {
  const text = payoutSetupCopy(locale);
  if (info.inside_window && info.days_remaining !== null) {
    return fillPayoutCopy(text[daysRemainingKey(locale, info.days_remaining)], { n: info.days_remaining });
  }
  return text.bankWindowClosed;
}

export function bankWindowCopy(info: BankWindowInfo, locale: unknown = 'en'): string {
  const text = payoutSetupCopy(locale);
  if (info.status === 'unavailable') return text.bankStatusUnknown;
  if (info.first_verified_at) return text.bankNeedsRestore;
  switch (info.enrollment) {
    case 'existing_agreement':
      return text.bankExistingAgreement;
    case 'manual':
      return text.bankManual;
    case 'owner_site':
      return text.bankOwnerSite;
    case 'transition_scheduled':
      return info.transition_on
        ? fillPayoutCopy(text.bankTransitionScheduled, { date: formatCalendarDay(info.transition_on, payoutDateLocale(locale)) })
        : text.bankExistingAgreement;
    case 'not_launched':
      return text.bankLaunchUnknown;
    case 'enrolled':
      return info.grace_anchor_at === null ? text.bankAccessNotYetGranted : countdown(info, locale);
    default:
      break;
  }
  if (!info.launch_at) return text.bankLaunchUnknown;
  if (info.inside_window && info.days_remaining !== null) return countdown(info, locale);
  const launchedMs = Date.parse(info.launch_at);
  if (!info.policy_version && Number.isFinite(launchedMs) && launchedMs < FIRST_LAUNCH_MS) {
    return text.bankExistingAgreement;
  }
  return text.bankWindowClosed;
}

/**
 * The locations the red banner lists. The banner's styling says a deadline
 * is close, so it lists a location only while one runs or is scheduled, or
 * when a verified bank broke. The card on My Site keeps every location's line.
 */
export function bannerLocations<T extends BankWindowInfo & { applicable: boolean; ready: boolean }>(locations: T[]): T[] {
  return locations.filter(l => l.applicable && !l.ready && (!l.enrollment || !!l.first_verified_at
    || l.enrollment === 'enrolled' || l.enrollment === 'transition_scheduled'));
}

export function bankBannerHeadline(locations: Array<Pick<BankWindowInfo, 'start_basis' | 'grace_anchor_at'>>, locale: unknown = 'en'): string {
  const text = payoutSetupCopy(locale);
  if (locations.some(l => l.grace_anchor_at !== undefined)) return text.bankBannerWindowHeadline;
  return locations.every(l => !l.start_basis || l.start_basis === 'launch')
    ? text.bankBannerLaunchHeadline : text.bankBannerWindowHeadline;
}
